import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { MessageSquare, Activity, Calendar, TrendingUp, ClipboardCheck } from 'lucide-react';
import { DashboardProps } from '../types/components';
import { Button, Card, CardContent, CardHeader, CardTitle, Tag } from '../components/ui';

const Dashboard: React.FC<DashboardProps> = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const hasTestContributorRole = user?.roles?.includes('testContributor');

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <div className="bg-rm-primary-700 text-white rounded-card shadow-card p-8">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-4xl font-semibold">
              Welcome back, {user?.displayName || 'User'}!
            </h1>
            <p className="mt-4 text-lg text-white/90">
              Ready to continue your mental health journey today?
            </p>
          </div>
        </div>
        
        <div className="mt-8 flex gap-4">
          <Button variant="primary" className="inline-flex items-center">
            <MessageSquare className="w-4 h-4 mr-2" />
            Start Chat
          </Button>
          <Button variant="ghost" className="text-white hover:bg-white/10">
            View Progress
          </Button>
        </div>
      </div>

      {/* Program Support Call Section */}
      <Card className="p-6">
        <CardHeader className="pb-4">
          <CardTitle>Renewmyday Today</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-3 items-center">
            <div>
              <p className="text-sm text-rm-neutral-600">Every</p>
              <p className="font-medium text-rm-ink-900">Tuesday</p>
            </div>
            <div>
              <p className="text-sm text-rm-neutral-600">Time</p>
              <p className="font-semibold text-rm-ink-900">17:00 PM</p>
            </div>
            <div className="sm:justify-self-end">
              <Button variant="primary">Join Session</Button>
            </div>
          </div>
          <p className="mt-4 text-sm text-rm-neutral-600">
            Registration is not necessary
          </p>
        </CardContent>
      </Card>

      {/* Dashboard Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card className="p-6">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Recent Activity</CardTitle>
              <Activity className="w-5 h-5 text-rm-teal-600" />
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-rm-ink-900">Your recent activities will appear here.</p>
            <div className="mt-4 flex gap-2">
              <Tag variant="support">Support</Tag>
              <Tag variant="coaching">Coaching</Tag>
            </div>
          </CardContent>
        </Card>

        <Card className="p-6">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Mood Tracking</CardTitle>
              <TrendingUp className="w-5 h-5 text-rm-teal-600" />
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-rm-ink-900">Track your daily mood and emotions.</p>
            <div className="mt-4">
              <Button variant="ghost" className="w-full">View Mood History</Button>
            </div>
          </CardContent>
        </Card>

        <Card className="p-6">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Upcoming Sessions</CardTitle>
              <Calendar className="w-5 h-5 text-rm-teal-600" />
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-rm-ink-900">Your scheduled sessions will appear here.</p>
            <div className="mt-4">
              <Button variant="secondary" className="w-full">Book Session</Button>
            </div>
          </CardContent>
        </Card>

        {/* Test Evaluation Card - Only for testContributor role */}
        {hasTestContributorRole && (
          <Card className="p-6">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Test Evaluation</CardTitle>
                <ClipboardCheck className="w-5 h-5 text-rm-teal-600" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-rm-ink-900">View test evaluation dashboard and results.</p>
              <div className="mt-4">
                <Button 
                  variant="primary" 
                  className="w-full"
                  onClick={() => navigate('/test-evaluation')}
                >
                  View Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
